import { useCallback, useEffect, useRef, useState } from 'react';
import type { IncidentBlock, StateResponse } from '../lib/api.js';
import { fetchState } from '../lib/api.js';
import {
  DEFAULT_STALE_AFTER_MS,
  type DashboardResource,
  createDashboardResource,
  markResourceError,
  markResourceLoading,
  markResourceSuccess,
  updateResourceStaleness,
} from '../lib/dashboardResource.js';

interface UseStateStreamOptions {
  pollIntervalMs?: number;
  staleAfterMs?: number;
}

// No SSE in the local runtime — state.json snapshots regenerate every 30s.
// Pure polling at 30s matches the snapshot TTL and keeps the UI current.

export interface UseStateStreamResult {
  state: DashboardResource<StateResponse>;
  incident: DashboardResource<IncidentBlock | null>;
  refreshState: () => Promise<void>;
}

export function useStateStream(
  onState: (state: StateResponse) => void,
  onIncident?: (incident: IncidentBlock | null) => void,
  options: UseStateStreamOptions = {},
): UseStateStreamResult {
  const pollIntervalMs = options.pollIntervalMs ?? 30_000;
  const staleAfterMs = options.staleAfterMs ?? DEFAULT_STALE_AFTER_MS;
  const mountedRef = useRef(true);

  const [state, setState] = useState(createDashboardResource<StateResponse>());
  const [incident, setIncident] = useState(createDashboardResource<IncidentBlock | null>());

  const refreshState = useCallback(async () => {
    if (!mountedRef.current) {
      return;
    }

    setState((resource) => markResourceLoading(resource));
    try {
      const payload = await fetchState();
      if (!mountedRef.current) {
        return;
      }
      onState(payload);
      onIncident?.(payload.incident);
      setState((resource) => markResourceSuccess(resource, payload, payload.fetched_at));
      setIncident((resource) =>
        markResourceSuccess(resource, payload.incident, payload.fetched_at),
      );
    } catch (error) {
      if (!mountedRef.current) {
        return;
      }
      setState((resource) => markResourceError(resource, error));
    }
  }, [onState, onIncident]);

  useEffect(() => {
    mountedRef.current = true;

    refreshState();
    const poller = setInterval(() => {
      refreshState();
    }, pollIntervalMs);

    const freshness = setInterval(() => {
      setState((resource) => updateResourceStaleness(resource, staleAfterMs));
      setIncident((resource) => updateResourceStaleness(resource, staleAfterMs));
    }, 1_000);

    return () => {
      mountedRef.current = false;
      clearInterval(poller);
      clearInterval(freshness);
    };
  }, [pollIntervalMs, refreshState, staleAfterMs]);

  return {
    state,
    incident,
    refreshState,
  };
}
